import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Dropdown, Input, Modal, Skeleton, Tooltip, message } from "antd";
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CaretDownFilled,
  CaretUpFilled,
  CheckOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  MinusCircleOutlined,
  MoreOutlined,
  SearchOutlined,
  SwapOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import BrandTemplateApi, { BrandTemplateDto } from "../../api/BrandTemplateApi";
import { env } from "../../env/env";
import { AppContext } from "../../AppContext";
import BatchBar from "../common/BatchBar";
import SelectableCard from "../common/SelectableCard";
import RendererThumb from "../common/RendererThumb";
import "./BrandTemplates.scss";
import { getSizeDimensions, getSizeLabel } from "../../util/utils";

type Category = "MENU" | "DMB" | "CUSTOM";
type SortKey = "recently_edited" | "oldest_edited" | "name_asc" | "name_desc";
type ViewMode = "grid" | "list";
type ColumnKey = "name" | "type" | "lastEdited" | "editedBy";
type ColumnSortDir = "asc" | "desc";

const CATEGORY_LABELS: Record<Category, string> = {
  MENU: "Menu",
  DMB: "Digital Menu Board",
  CUSTOM: "Custom Size",
};

const SORT_ITEMS: { key: SortKey; label: string; icon: JSX.Element }[] = [
  { key: "recently_edited", label: "Recently Edited", icon: <ClockCircleOutlined /> },
  { key: "oldest_edited", label: "Oldest Edited", icon: <ClockCircleOutlined /> },
  { key: "name_asc", label: "Alphabetical (A-Z)", icon: <ArrowUpOutlined /> },
  { key: "name_desc", label: "Alphabetical (Z-A)", icon: <ArrowDownOutlined /> },
];

function categoryOf(productType: string): Category {
  if (productType.includes("custom")) return "CUSTOM";
  if (productType === "digital-menu-board" || productType === "digital-menu-board-portrait") return "DMB";
  return "MENU";
}

function TemplateSize({ template }: { template: BrandTemplateDto }) {
  const label = getSizeLabel(template.productType);
  const unitSuffix = template.sizeUnit ? ` ${template.sizeUnit.toLowerCase()}` : "";
  if (label) {
    return (
      <span className="brand-templates-size">
        <span className="size-label">{label}</span> • {getSizeDimensions(template.productType)}
        {unitSuffix}
      </span>
    );
  }
  if (template.width && template.height) {
    return (
      <span className="brand-templates-size">
        <span className="size-label">Custom</span> • {template.width} x {template.height}
        {unitSuffix}
      </span>
    );
  }
  return null;
}

function ts(date?: string): number {
  if (!date) return 0;
  const t = Date.parse(date);
  return Number.isNaN(t) ? 0 : t;
}

function formatRelativeDate(date?: string): string {
  if (!date) return "—";
  const t = ts(date);
  if (!t) return "—";
  const d = new Date(t);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfToday - startOfDate) / 86400000);
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatEditedAt(date?: string): string {
  if (!date) return "—";
  const t = ts(date);
  if (!t) return "—";
  const d = new Date(t);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfToday - startOfDate) / 86400000);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (dayDiff === 0) return `${time} today`;
  if (dayDiff === 1) return `${time} yesterday`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${time} ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  }
  return `${time} ${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}

function Thumb({ id, name }: { id?: string; name: string }) {
  return (
    <RendererThumb
      id={id}
      alt={name}
      className="brand-templates-thumb-img"
      fallback={<div className="brand-templates-thumb-fallback">{name}</div>}
    />
  );
}

export default function BrandTemplates() {
  const { location } = useContext(AppContext);
  const [templates, setTemplates] = useState<BrandTemplateDto[]>([]);
  const [loading, setLoading] = useState(false);

  const [draftCategories, setDraftCategories] = useState<Category[]>([]);
  const [activeCategories, setActiveCategories] = useState<Category[]>([]);
  const [categoryQuery, setCategoryQuery] = useState("");
  const [categoryOpen, setCategoryOpen] = useState(false);

  const [sortKey, setSortKey] = useState<SortKey>("recently_edited");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const [columnSort, setColumnSort] = useState<{ key: ColumnKey; dir: ColumnSortDir } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const load = useCallback(() => {
    if (!location?.internalId) return;
    setLoading(true);
    BrandTemplateApi.listByLocation(location.internalId)
      .then((data) => setTemplates(data || []))
      .catch(() => message.error("Failed to load brand templates"))
      .finally(() => setLoading(false));
  }, [location?.internalId]);

  useEffect(() => {
    load();
  }, [load]);

  const availableCategories: Category[] = useMemo(() => {
    const seen = new Set<Category>();
    templates.forEach((t) => seen.add(categoryOf(t.productType)));
    const all: Category[] = ["MENU", "DMB", "CUSTOM"];
    return all.filter((c) => seen.has(c));
  }, [templates]);

  const filteredCategoryRows = useMemo(() => {
    const q = categoryQuery.trim().toLowerCase();
    if (!q) return availableCategories;
    return availableCategories.filter((c) =>
      CATEGORY_LABELS[c].toLowerCase().includes(q)
    );
  }, [availableCategories, categoryQuery]);

  const filtered = useMemo(() => {
    let list = templates;
    if (activeCategories.length > 0) {
      list = list.filter((t) => activeCategories.includes(categoryOf(t.productType)));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((t) => (t.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [templates, activeCategories, search]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (viewMode === "list" && columnSort) {
      const { key, dir } = columnSort;
      const mul = dir === "asc" ? 1 : -1;
      list.sort((a, b) => {
        if (key === "name") return mul * (a.name || "").localeCompare(b.name || "");
        if (key === "type") return mul * CATEGORY_LABELS[categoryOf(a.productType)].localeCompare(CATEGORY_LABELS[categoryOf(b.productType)]);
        if (key === "lastEdited") return mul * (ts(a.updatedDate) - ts(b.updatedDate));
        if (key === "editedBy") return mul * (a.lastEditorName || "").localeCompare(b.lastEditorName || "");
        return 0;
      });
      return list;
    }
    if (sortKey === "recently_edited") list.sort((a, b) => ts(b.updatedDate) - ts(a.updatedDate));
    else if (sortKey === "oldest_edited") list.sort((a, b) => ts(a.updatedDate) - ts(b.updatedDate));
    else if (sortKey === "name_asc") list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortKey === "name_desc") list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    return list;
  }, [filtered, sortKey, viewMode, columnSort]);

  const openCategoryDropdown = () => {
    setDraftCategories(activeCategories);
    setCategoryQuery("");
    setCategoryOpen(true);
  };

  const toggleDraftCategory = (cat: Category) => {
    setDraftCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const applyCategories = () => {
    setActiveCategories(draftCategories);
    setCategoryOpen(false);
  };

  const clearCategories = () => {
    setDraftCategories([]);
    setActiveCategories([]);
  };

  const openTemplate = (template: BrandTemplateDto) => {
    if (!template.id) return;
    window.location.href = `${env.timberContextPath}/design?parentId=${encodeURIComponent(template.id)}&fromSample=true`;
  };

  const editTemplate = (template: BrandTemplateDto) => {
    if (!template.id) return;
    window.location.href = `${env.timberContextPath}/design?id=${encodeURIComponent(template.id)}`;
  };

  const copyTemplate = async (template: BrandTemplateDto) => {
    if (!template.id || !template.brandKitId) return;
    try {
      const resp = await BrandTemplateApi.copy(template.brandKitId, template.id);
      if (!resp.ok) throw new Error();
      message.success("Brand template copied");
      load();
    } catch {
      message.error("Failed to copy brand template");
    }
  };

  const downloadTemplate = async (template: BrandTemplateDto) => {
    if (!template.id) return;
    try {
      const res = await fetch(`${env.rendererPath}/design/download/${template.id}.pdf`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = res.headers.get("filename") || `${template.name || "brand-template"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to download brand template");
    }
  };

  const downloadIds = async (ids: string[]) => {
    for (const id of ids) {
      const t = templates.find((x) => x.id === id);
      if (t) await downloadTemplate(t);
    }
  };

  const deleteIds = async (ids: string[]) => {
    if (ids.length === 0) return;
    const results = await Promise.allSettled(ids.map((id) => BrandTemplateApi.delete(id)));
    const okIds = ids.filter((_, i) => results[i].status === "fulfilled" && (results[i] as PromiseFulfilledResult<Response>).value.ok);
    if (okIds.length < ids.length) {
      message.error(`Failed to delete ${ids.length - okIds.length} of ${ids.length} brand template(s)`);
    } else {
      message.success(okIds.length === 1 ? "Brand template deleted" : `${okIds.length} brand templates deleted`);
    }
    if (okIds.length > 0) {
      setTemplates((prev) => prev.filter((t) => !okIds.includes(t.id || "")));
      setSelectedIds((prev) => prev.filter((id) => !okIds.includes(id)));
    }
  };

  const onBulkDownload = () => downloadIds(selectedIds);

  const onBulkDelete = () => {
    if (selectedIds.length === 0) return;
    Modal.confirm({
      title: selectedIds.length === 1 ? "Delete brand template?" : `Delete ${selectedIds.length} brand templates?`,
      content: "This cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => deleteIds([...selectedIds]),
    });
  };

  const onCancelSelection = () => setSelectedIds([]);

  const deleteTemplate = (template: BrandTemplateDto) => {
    if (!template.id) return;
    Modal.confirm({
      title: "Delete brand template?",
      content: `"${template.name}" will be permanently deleted.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const resp = await BrandTemplateApi.delete(template.id!);
        if (!resp.ok) {
          message.error("Failed to delete brand template");
          return Promise.reject();
        }
        message.success("Brand template deleted");
        setTemplates((prev) => prev.filter((t) => t.id !== template.id));
        setSelectedIds((prev) => prev.filter((id) => id !== template.id));
      },
    });
  };

  const removeFromBrandTemplate = (template: BrandTemplateDto) => {
    if (!template.id) return;
    Modal.confirm({
      title: "Remove from brand kit?",
      content:
        "This design will move back to Saved Designs. It won't be available as a template anymore, but nothing else will change.",
      okText: "Remove from Brand Templates",
      onOk: async () => {
        try {
          await BrandTemplateApi.removeFromBrandTemplates(template.id!);
        } catch {
          message.error("Failed to remove from brand templates");
          return Promise.reject();
        }
        message.success("Removed from brand templates");
        setTemplates((prev) => prev.filter((t) => t.id !== template.id));
        setSelectedIds((prev) => prev.filter((id) => id !== template.id));
      },
    });
  };

  const cardMenuItems = (t: BrandTemplateDto) => [
    {
      key: "header",
      type: "group" as const,
      label: (
        <div className="brand-templates-card-menu-header">
          {[
            CATEGORY_LABELS[categoryOf(t.productType)],
            getSizeLabel(t.productType),
          ]
            .filter(Boolean)
            .join(" ")}
          {t.updatedDate && (
            <>
              {" • "}
              <span>Edited {formatEditedAt(t.updatedDate)}</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit This Template",
      onClick: () => editTemplate(t),
    },
    {
      key: "copy",
      icon: <CopyOutlined />,
      label: "Make a copy",
      onClick: () => copyTemplate(t),
    },
    {
      key: "download",
      icon: <DownloadOutlined />,
      label: "Download",
      onClick: () => downloadTemplate(t),
    },
    {
      key: "removeFromBrandTemplate",
      icon: <MinusCircleOutlined />,
      label: "Remove from Brand Templates",
      onClick: () => removeFromBrandTemplate(t),
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      onClick: () => deleteTemplate(t),
    },
  ];

  const toggleColumnSort = (key: ColumnKey) => {
    setColumnSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const toggleSelect = (id?: string) => {
    if (!id) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allVisibleIds = sorted.map((t) => t.id).filter((x): x is string => !!x);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.includes(id));
  const someSelected = !allSelected && allVisibleIds.some((id) => selectedIds.includes(id));

  const toggleSelectAll = (checked: boolean) => {
    if (checked) setSelectedIds(allVisibleIds);
    else setSelectedIds([]);
  };

  const sortMenu = (
    <div className="brand-templates-sort-menu">
      {SORT_ITEMS.map((item) => (
        <div
          key={item.key}
          className={`brand-templates-sort-item${sortKey === item.key ? " selected" : ""}`}
          onClick={() => setSortKey(item.key)}
        >
          <span className="brand-templates-sort-icon">{item.icon}</span>
          <span className="brand-templates-sort-label">{item.label}</span>
          {sortKey === item.key && (
            <span className="brand-templates-sort-check"><CheckOutlined /></span>
          )}
        </div>
      ))}
    </div>
  );

  const categoryMenu = (
    <div className="brand-templates-category-menu" onClick={(e) => e.stopPropagation()}>
      <div className="brand-templates-category-search">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search categories"
          value={categoryQuery}
          onChange={(e) => setCategoryQuery(e.target.value)}
        />
      </div>
      <div className="brand-templates-category-list">
        <label className="brand-templates-category-row">
          <Checkbox
            checked={draftCategories.length === availableCategories.length && availableCategories.length > 0}
            indeterminate={draftCategories.length > 0 && draftCategories.length < availableCategories.length}
            onChange={(e) =>
              setDraftCategories(e.target.checked ? [...availableCategories] : [])
            }
          />
          <AppstoreOutlined />
          <span>All Categories</span>
        </label>
        {filteredCategoryRows.map((cat) => (
          <label key={cat} className="brand-templates-category-row">
            <Checkbox
              checked={draftCategories.includes(cat)}
              onChange={() => toggleDraftCategory(cat)}
            />
            <span>{CATEGORY_LABELS[cat]}</span>
          </label>
        ))}
        {filteredCategoryRows.length === 0 && (
          <div className="brand-templates-category-empty">No categories</div>
        )}
      </div>
      <div className="brand-templates-category-actions">
        <Button onClick={clearCategories}>Clear</Button>
        <Button type="primary" onClick={applyCategories}>Apply</Button>
      </div>
    </div>
  );

  const categoryLabel =
    activeCategories.length === 0
      ? "Category"
      : activeCategories.length === 1
      ? CATEGORY_LABELS[activeCategories[0]]
      : `Category (${activeCategories.length})`;

  const currentSortItem = SORT_ITEMS.find((s) => s.key === sortKey)!;

  const headerSortIcon = (key: ColumnKey) => {
    if (!columnSort || columnSort.key !== key) {
      return (
        <span className="brand-templates-table-sort-icon">
          <CaretUpFilled />
          <CaretDownFilled />
        </span>
      );
    }
    return (
      <span className="brand-templates-table-sort-icon active">
        {columnSort.dir === "asc" ? <CaretUpFilled /> : <CaretDownFilled />}
      </span>
    );
  };

  return (
    <div className="brand-templates-page">
      <div className="brand-templates-header">
        <div className="brand-templates-header-left">
          <h3 className="brand-templates-heading">Brand Templates</h3>
          <Dropdown
            open={categoryOpen}
            onOpenChange={(open) => (open ? openCategoryDropdown() : setCategoryOpen(false))}
            trigger={["click"]}
            overlay={categoryMenu}
          >
            <Button
              className={`brand-templates-pill${activeCategories.length > 0 ? " active" : ""}`}
            >
              {categoryLabel} <DownOutlined />
            </Button>
          </Dropdown>
          <Dropdown trigger={["click"]} overlay={sortMenu}>
            <Button className="brand-templates-pill">
              <SwapOutlined rotate={90} />
              <span style={{ marginLeft: 6 }}>{currentSortItem.label}</span>
              <DownOutlined />
            </Button>
          </Dropdown>
        </div>
        <div className="brand-templates-header-right">
          <Input
            allowClear
            className="brand-templates-search"
            prefix={<SearchOutlined />}
            placeholder="Search files"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="brand-templates-view-toggle">
            <Tooltip title="Grid view">
              <button
                type="button"
                className={`brand-templates-view-btn${viewMode === "grid" ? " active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
              >
                <AppstoreOutlined />
              </button>
            </Tooltip>
            <Tooltip title="List view">
              <button
                type="button"
                className={`brand-templates-view-btn${viewMode === "list" ? " active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="List view"
              >
                <UnorderedListOutlined />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {loading ? (
        <Skeleton active />
      ) : templates.length === 0 ? (
        <div className="brand-templates-empty">
          <p>No brand templates yet for this location.</p>
          <p>Create one from Account Settings → Brand Management.</p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="brand-templates-empty">
          <p>No brand templates match your filters.</p>
        </div>
      ) : viewMode === "grid" ? (
        <ul className="brand-templates-grid">
          {sorted.map((t, idx) => {
            const checked = !!t.id && selectedIds.includes(t.id);
            return (
              <SelectableCard
                key={t.id || idx}
                selected={checked}
                className="brand-templates-card"
                onMediaClick={() => openTemplate(t)}
                selectionSlot={<Checkbox checked={checked} onChange={() => toggleSelect(t.id)} />}
                actionsSlot={
                  <Dropdown
                    className="desktop-design-menu more-btn-ops"
                    trigger={["click"]}
                    overlayClassName="brand-templates-card-menu"
                    menu={{ items: cardMenuItems(t) }}
                  >
                    <MoreOutlined />
                  </Dropdown>
                }
                mediaSlot={<Thumb id={t.id} name={t.name} />}
                footerSlot={
                  <>
                    <span title={t.name}>{t.name || `Menu ${idx + 1}`}</span>
                    <TemplateSize template={t} />
                  </>
                }
              />
            );
          })}
        </ul>
      ) : (
        <div className="brand-templates-table" role="table">
          <div className="brand-templates-table-head" role="row">
            <div className="brand-templates-table-col col-select">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
            </div>
            <div
              className="brand-templates-table-col col-name sortable"
              role="columnheader"
              onClick={() => toggleColumnSort("name")}
            >
              <span>Name</span>{headerSortIcon("name")}
            </div>
            <div
              className="brand-templates-table-col col-type sortable"
              role="columnheader"
              onClick={() => toggleColumnSort("type")}
            >
              <span>Type</span>{headerSortIcon("type")}
            </div>
            <div
              className="brand-templates-table-col col-last sortable"
              role="columnheader"
              onClick={() => toggleColumnSort("lastEdited")}
            >
              <span>Last Edited</span>{headerSortIcon("lastEdited")}
            </div>
            <div
              className="brand-templates-table-col col-editor sortable"
              role="columnheader"
              onClick={() => toggleColumnSort("editedBy")}
            >
              <span>Edited by</span>{headerSortIcon("editedBy")}
            </div>
            <div className="brand-templates-table-col col-actions" />
          </div>
          {sorted.map((t, idx) => {
            const checked = !!t.id && selectedIds.includes(t.id);
            return (
              <div className="brand-templates-table-row" role="row" key={t.id || idx}>
                <div className="brand-templates-table-col col-select" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={checked} onChange={() => toggleSelect(t.id)} />
                </div>
                <div
                  className="brand-templates-table-col col-name clickable"
                  onClick={() => openTemplate(t)}
                >
                  <div className="brand-templates-row-thumb">
                    <Thumb id={t.id} name={t.name} />
                  </div>
                  <span className="brand-templates-row-name" title={t.name}>
                    {t.name || `Menu ${idx + 1}`}
                  </span>
                </div>
                <div className="brand-templates-table-col col-type">
                  {CATEGORY_LABELS[categoryOf(t.productType)]}
                  <TemplateSize template={t} />
                </div>
                <div className="brand-templates-table-col col-last">
                  {formatRelativeDate(t.updatedDate)}
                </div>
                <div className="brand-templates-table-col col-editor">
                  {t.lastEditorName || "—"}
                </div>
                <div className="brand-templates-table-col col-actions" onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    trigger={["click"]}
                    overlayClassName="brand-templates-card-menu"
                    menu={{ items: cardMenuItems(t) }}
                  >
                    <button type="button" className="brand-templates-row-more" aria-label="More">
                      <MoreOutlined />
                    </button>
                  </Dropdown>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedIds.length > 0 && (
        <BatchBar
          count={selectedIds.length}
          onCancel={onCancelSelection}
          actions={[
            ...(selectedIds.length === 1
              ? [{ key: "download", icon: <DownloadOutlined />, label: "Download", onClick: onBulkDownload }]
              : []),
            { key: "delete", icon: <DeleteOutlined />, label: "Delete", onClick: onBulkDelete },
          ]}
        />
      )}
    </div>
  );
}
